// Refactored unified implementation
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import da from 'date-fns/locale/da';
import { DateCalendar } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Button, CircularProgress, TextField } from '@mui/material';
import BookingCard from 'components/BookingCard';
import TimeSelector from 'components/TimeSelector';
import * as fns from 'date-fns';
import { DayId, MEETING_TYPE_CONFIGS, MeetingType, MeetingTypeConfig, resolveMeetingType } from 'config/meetingTypes';
import { BookingAPI } from 'utils/BookingAPI';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { loadingEnd, loadingStart, setBookedNow } from 'store/globalSlice';
import { validation } from 'utils/validation';
import { resetBooking, setBooking, setServiceDetails } from 'store/bookingSlice';
import ConfirmedBookingsPage from 'pages/ConfirmedBookingsPage';
import ErrorBookingModal from 'modals/ErrorBookingModal';

interface ContactField { value: string; error: string | null }
interface ContactState { email: ContactField; emailRepeat: ContactField; phone: ContactField; phoneRepeat: ContactField; }
interface DaySelection { date: Date | null; time: Date | null }
type SelectionsState = Record<DayId, DaySelection | undefined>;
type AvailabilityState = Record<DayId, Date[] | undefined>;
interface DayErrors { date?: string | null; time?: string | null }
type ErrorState = { [dayId: string]: DayErrors } & { contact?: string[] };
type ContactFieldKey = keyof ContactState;
const defaultContactState: ContactState = { email: { value: '', error: null }, emailRepeat: { value: '', error: null }, phone: { value: '', error: null }, phoneRepeat: { value: '', error: null } };

const NewBookingPage: FC = () => {
    const dispatch = useDispatch();
    const participantViewState = useSelector((s: RootState) => s.user.participantViewState);
    const token = useSelector((s: RootState) => s.global.authToken);
    const meetingType = resolveMeetingType(participantViewState as any);
    const meetingConfig: MeetingTypeConfig | null = meetingType ? MEETING_TYPE_CONFIGS[meetingType] : null;
    const serviceDetails = useSelector((s: RootState) => s.booking.serviceDetails);
    const emptySelections: SelectionsState = { INFO: undefined, V0: undefined, V1: undefined, V2: undefined, V3: undefined, V4: undefined };
    const [availability, setAvailability] = useState<AvailabilityState>({ INFO: undefined, V0: undefined, V1: undefined, V2: undefined, V3: undefined, V4: undefined });
    const [selections, setSelections] = useState<SelectionsState>(() => {
        if (!meetingConfig) return emptySelections;
        const base = { ...emptySelections } as SelectionsState;
        meetingConfig.days.forEach(d => { base[d.id] = { date: null, time: null }; });
        return base;
    });
    // Ensure controlled selection objects exist for each configured day
    useEffect(() => {
        if (!meetingConfig) return;
        setSelections(prev => {
            let changed = false;
            const nextState: SelectionsState = { ...prev };
            meetingConfig.days.forEach(d => {
                if (!nextState[d.id]) {
                    nextState[d.id] = { date: null, time: null };
                    changed = true;
                }
            });
            return changed ? nextState : prev;
        });
    }, [meetingConfig]);
    const [errors, setErrors] = useState<ErrorState>({});
    const [contact, setContact] = useState<ContactState>(defaultContactState);
    const [loadingDays, setLoadingDays] = useState<Record<string, boolean>>({});
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [booked, setBooked] = useState(false);
    const [bookingConflict, setBookingConflict] = useState(false);

    const fetchServiceDetails = useCallback(async () => {
        if (!token || !meetingConfig) return;
        setLoadingDetails(true);
        try {
            const uniqueKeys = Array.from(new Set(meetingConfig.days.map(d => d.serviceKey)));
            await Promise.all(uniqueKeys.map(async key => {
                if (serviceDetails[key]) return;
                let fn: (() => Promise<any>) | undefined;
                switch (key) {
                case 'INFO': fn = () => BookingAPI.getInfoMeetingServiceDetails(token); break;
                case 'V0': fn = () => BookingAPI.getV0ServiceDetails(token); break;
                case 'V1': fn = () => BookingAPI.getV1ServiceDetails(token); break;
                case 'V2': fn = () => BookingAPI.getV2ServiceDetails(token); break;
                case 'V3': fn = () => BookingAPI.getV3ServiceDetails(token); break;
                case 'V4': fn = () => BookingAPI.getV4ServiceDetails(token); break;
                default: break;
                }
                if (fn) { const details = await fn(); dispatch(setServiceDetails({ key: key as any, details })); }
            }));
        } finally { setLoadingDetails(false); }
    }, [dispatch, meetingConfig, serviceDetails, token]);

    const fetchAvailabilityForDay = useCallback(async (dayId: DayId, baseDate: Date): Promise<Date[] | undefined> => {
        if (!token || !meetingConfig) return undefined;
        setLoadingDays(ld => ({ ...ld, [dayId]: true }));
        try {
            const dayCfg = meetingConfig.days.find(d => d.id === dayId)!;
            const body = { year: baseDate.getFullYear(), month: baseDate.getMonth() + 1 };
            let response: any;
            switch (dayCfg.serviceKey) {
            case 'INFO': response = await BookingAPI.getInfoMeetingStaffV0Availability(body, token); break;
            case 'V0': response = await BookingAPI.getV0StaffAvailability(body, token); break;
            case 'V1':
            case 'V2': response = await BookingAPI.getV1V2StaffAvailability(body, token); break; // returns both V1 & V2
            case 'V3':
            case 'V4': response = await BookingAPI.getV3V4StaffAvailability(body, token); break; // returns both V3 & V4
            default: break;
            }
            let items: string[] = [];
            if (response) {
                if (response.infoMeetingItems) items = response.infoMeetingItems.map((i: any) => i.timeSlotStart);
                if (response.v0MeetingItems) items = response.v0MeetingItems.map((i: any) => i.timeSlotStart);
                // Paired responses: populate both day availabilities at once
                if (response.v1MeetingItems || response.v2MeetingItems) {
                    const v1Dates = (response.v1MeetingItems || []).map((i: any) => new Date(i.timeSlotStart || i.timeSlotStart || i.timeSlotStart));
                    const v2Dates = (response.v2MeetingItems || []).map((i: any) => new Date(i.timeSlotStart || i.timeSlotStart || i.timeSlotStart));
                    setAvailability(a => ({ ...a, V1: v1Dates.filter((dt: Date) => fns.isAfter(dt, new Date())), V2: v2Dates.filter((dt: Date) => fns.isAfter(dt, new Date())) }));
                }
                if (response.v3MeetingItems || response.v4MeetingItems) {
                    const v3Dates = (response.v3MeetingItems || []).map((i: any) => new Date(i.timeSlotStart || i.timeSlotStart || i.timeSlotStart));
                    const v4Dates = (response.v4MeetingItems || []).map((i: any) => new Date(i.timeSlotStart || i.timeSlotStart || i.timeSlotStart));
                    setAvailability(a => ({ ...a, V3: v3Dates.filter((dt: Date) => fns.isAfter(dt, new Date())), V4: v4Dates.filter((dt: Date) => fns.isAfter(dt, new Date())) }));
                }
                if (response.v1MeetingItems && dayCfg.serviceKey === 'V1') items = response.v1MeetingItems.map((i: any) => i.timeSlotStart);
                if (response.v2MeetingItems && dayCfg.serviceKey === 'V2') items = response.v2MeetingItems.map((i: any) => i.timeSlotStart);
                if (response.v3MeetingItems && dayCfg.serviceKey === 'V3') items = response.v3MeetingItems.map((i: any) => i.timeSlotStart);
                if (response.v4MeetingItems && dayCfg.serviceKey === 'V4') items = response.v4MeetingItems.map((i: any) => i.timeSlotStart);
            }
            const futureDates = items.map(i => new Date(i)).filter(dt => fns.isAfter(dt, new Date()));
            setAvailability(a => ({ ...a, [dayId]: futureDates }));
            // Auto-pick earliest only for primary (non-dependent) days; dependent days selection handled separately
            setSelections(sel => {
                const cfg = meetingConfig.days.find(d => d.id === dayId);
                const current = sel[dayId];
                if (cfg?.dependsOnPrev) return sel;
                if (!current?.date && futureDates.length > 0) return { ...sel, [dayId]: { date: futureDates[0], time: null } };
                return sel;
            });
            return futureDates;
        } finally { setLoadingDays(ld => ({ ...ld, [dayId]: false })); }
    }, [meetingConfig, token]);

    // Derive dependent date from primary (gap + weekend adjustment)
    const computeDependentDate = useCallback((prevDate: Date, depCfg: { minGapDays?: number; weekendAdjust?: boolean; exactGap?: boolean; }) => {
        let target = fns.addDays(prevDate, depCfg.minGapDays || 0);
        if (depCfg.weekendAdjust) {
            const day = target.getDay();
            if (day === 0) target = fns.addDays(target, 1); // Sunday -> Monday
            else if (day === 6) target = fns.addDays(target, 2); // Saturday -> Monday
        }
        return target;
    }, []);
    useEffect(() => { fetchServiceDetails(); }, [fetchServiceDetails]);
    // Availability loads only via primary endpoint (paired endpoints hydrate both days)

    const onSelectDate = useCallback(async (date: Date | null, dayId: DayId) => {
        // Skip if selecting same day again
        if (date && selections[dayId]?.date && fns.isSameDay(selections[dayId]!.date!, date)) return;
        setSelections(sel => ({ ...sel, [dayId]: { date, time: null } }));
        setErrors(err => ({ ...err, [dayId]: { ...(err[dayId] || {}), date: null } }));
        // Dependent auto-selection handled in effect below
    }, [selections]);

    // Ref: track which months already fetched for primary; prevents duplicate network calls

    // Track which months have been loaded for each day to avoid duplicate fetch loops
    const loadedMonthsRef = useRef<Record<DayId, Set<string>>>({ INFO: new Set(), V0: new Set(), V1: new Set(), V2: new Set(), V3: new Set(), V4: new Set() });

    // Initial fetch (primary day current month only)
    useEffect(() => {
        if (!meetingConfig) return;
        const primary = meetingConfig.days[0];
        const key = fns.format(new Date(), 'yyyy-MM');
        if (!loadedMonthsRef.current[primary.id].has(key)) {
            loadedMonthsRef.current[primary.id].add(key);
            fetchAvailabilityForDay(primary.id, new Date());
        }
    }, [fetchAvailabilityForDay, meetingConfig]);

    // Keep dependent selection in sync with primary (no extra fetching)
    useEffect(() => {
        if (!meetingConfig) return;
        const primary = meetingConfig.days[0];
        const dependent = meetingConfig.days[1];
        if (!dependent?.dependsOnPrev) return;
        const primaryDate = selections[primary.id]?.date;
        if (!primaryDate) return;
        const expected = computeDependentDate(primaryDate, dependent);
        const currentDependent = selections[dependent.id]?.date;
        if (!currentDependent || !fns.isSameDay(currentDependent, expected)) {
            setSelections(sel => ({ ...sel, [dependent.id]: { date: expected, time: null } }));
        }
        // No further fetching: business rule limits us to initial month availability only.
    }, [computeDependentDate, meetingConfig, selections]);
    const onSelectTime = useCallback((time: Date, dayId: DayId) => { setSelections(sel => ({ ...sel, [dayId]: { ...(sel[dayId] || { date: null }), time } })); setErrors(err => ({ ...err, [dayId]: { ...(err[dayId] || {}), time: null } })); }, []);
    const onContactChange = useCallback((field: ContactFieldKey, value: string) => { setContact(c => ({ ...c, [field]: { value, error: null } })); }, []);

    const validate = useCallback((): boolean => {
        if (!meetingConfig) return false; const newErrors: ErrorState = {}; meetingConfig.days.forEach(d => { const sel = selections[d.id]; if (!sel?.date) newErrors[d.id] = { ...(newErrors[d.id] || {}), date: 'Vælg venligst dato' }; if (!sel?.time) newErrors[d.id] = { ...(newErrors[d.id] || {}), time: 'Vælg venligst tidspunkt' }; }); if (meetingConfig.needsContact) { const contactErrors: string[] = []; const emailErr = validation.isValidEmail(contact.email.value); if (emailErr) { contactErrors.push(emailErr); setContact(c => ({ ...c, email: { ...c.email, error: emailErr } })); } const emailRepeatErr = validation.isMatchEmail(contact.email.value, contact.emailRepeat.value); if (emailRepeatErr) { contactErrors.push(emailRepeatErr); setContact(c => ({ ...c, emailRepeat: { ...c.emailRepeat, error: emailRepeatErr } })); } const phoneErr = validation.isValidPhone(contact.phone.value); if (phoneErr) { contactErrors.push(phoneErr); setContact(c => ({ ...c, phone: { ...c.phone, error: phoneErr } })); } const phoneRepeatErr = validation.isMatchPhone(contact.phone.value, contact.phoneRepeat.value); if (phoneRepeatErr) { contactErrors.push(phoneRepeatErr); setContact(c => ({ ...c, phoneRepeat: { ...c.phoneRepeat, error: phoneRepeatErr } })); } if (contactErrors.length) newErrors.contact = contactErrors; } setErrors(newErrors); return Object.keys(newErrors).length === 0; }, [contact, meetingConfig, selections]);

    const formatDateTime = (d: Date, t: Date) => fns.format(d, 'yyyy-MM-dd') + 'T' + fns.format(t, 'HH:mm:ss');
    const buildPayloadAndBook = useCallback(async () => {
        if (!meetingConfig || !token) return;
        switch (meetingConfig.type) {
        case MeetingType.INFO: {
            const info = selections['INFO'];
            if (!info) return;
            const body = { infoMeetingStart: formatDateTime(info.date!, info.time!), email: contact.email.value, phone: contact.phone.value };
            const res = await BookingAPI.bookInfoMeetingAppointment(body, token);
            dispatch(setBooking({ dayId: 'INFO', title: res.infoMeetingBookingResponseItem.title, date: res.infoMeetingBookingResponseItem.date, duration: res.infoMeetingBookingResponseItem.duration, location: res.infoMeetingBookingResponseItem.location }));
            break;
        }
        case MeetingType.V0: {
            const v0 = selections['V0'];
            if (!v0) return;
            const body = { v0meetingstart: formatDateTime(v0.date!, v0.time!), email: contact.email.value, phone: contact.phone.value };
            const res = await BookingAPI.bookV0Appointment(body, token);
            dispatch(setBooking({ dayId: 'V0', title: res.v0MeetingBookingResponseItem.title, date: res.v0MeetingBookingResponseItem.date, duration: res.v0MeetingBookingResponseItem.duration, location: res.v0MeetingBookingResponseItem.location }));
            break;
        }
        case MeetingType.V1V2: {
            const v1 = selections['V1'];
            const v2 = selections['V2'];
            if (!v1 || !v2) return;
            const body = { V1MeetingStart: formatDateTime(v1.date!, v1.time!), V2MeetingStart: formatDateTime(v2.date!, v2.time!) };
            const res = await BookingAPI.bookV1V2Appointments(body, token);
            dispatch(resetBooking({ dayId: 'V0' }));
            dispatch(setBooking({ dayId: 'V1', title: res.v1MeetingBookingResponseItem.title, date: res.v1MeetingBookingResponseItem.date, duration: res.v1MeetingBookingResponseItem.duration, location: res.v1MeetingBookingResponseItem.location }));
            dispatch(setBooking({ dayId: 'V2', title: res.v2MeetingBookingResponseItem.title, date: res.v2MeetingBookingResponseItem.date, duration: res.v2MeetingBookingResponseItem.duration, location: res.v2MeetingBookingResponseItem.location }));
            break;
        }
        case MeetingType.V3V4: {
            const v3 = selections['V3'];
            const v4 = selections['V4'];
            if (!v3 || !v4) return;
            const body = { V3MeetingStart: formatDateTime(v3.date!, v3.time!), V4MeetingStart: formatDateTime(v4.date!, v4.time!) };
            const res = await BookingAPI.bookV3V4Appointments(body, token);
            dispatch(setBooking({ dayId: 'V3', title: res.v3MeetingBookingResponseItem.title, date: res.v3MeetingBookingResponseItem.date, duration: res.v3MeetingBookingResponseItem.duration, location: res.v3MeetingBookingResponseItem.location }));
            dispatch(setBooking({ dayId: 'V4', title: res.v4MeetingBookingResponseItem.title, date: res.v4MeetingBookingResponseItem.date, duration: res.v4MeetingBookingResponseItem.duration, location: res.v4MeetingBookingResponseItem.location }));
            break;
        }
        default: break;
        }
    }, [contact.email.value, contact.phone.value, dispatch, meetingConfig, selections, token]);

    const onSubmit = useCallback(async () => {
        if (!meetingConfig) return;
        if (!validate()) return;
        try {
            setSubmitting(true);
            dispatch(loadingStart());
            try {
                await buildPayloadAndBook();
                dispatch(setBookedNow(true));
                setBooked(true);
            } catch (e: any) {
                // 409 conflict -> slot already taken
                if (e?.response?.status === 409) {
                    setBookingConflict(true);
                    return;
                }
                throw e;
            }
        } finally {
            dispatch(loadingEnd());
            setSubmitting(false);
        }
    }, [buildPayloadAndBook, dispatch, meetingConfig, validate]);

    const selectionForDay = (dayId: DayId) => selections[dayId];
    const timesForDay = (dayId: DayId) => { const sel = selectionForDay(dayId); if (!sel?.date) return []; return (availability[dayId] || []).filter((d: Date) => fns.isSameDay(d, sel.date!)); };
    const showContact = meetingConfig?.needsContact;
    if (!meetingConfig) return null; if (booked) return <ConfirmedBookingsPage />;
    return (
        <div className={styles.content}>
            <ErrorBookingModal
                open={bookingConflict}
                onClose={() => setBookingConflict(false)}
                text={'Tidsrummet er desværre lige blevet optaget. Vælg venligst et andet tidspunkt.'}
            />
            <div className={styles.bookingRow}>
                {meetingConfig.days.map((day, index) => { const details = serviceDetails[day.serviceKey]; const sel = selectionForDay(day.id); const dayErrors = errors[day.id] || {}; const showCalendarSpinner = !!loadingDays[day.id]; const prevDay = index > 0 ? meetingConfig.days[index - 1] : null; let expectedDependentDate: Date | null = null; let hasSlotsForExpected = false; if (day.dependsOnPrev && prevDay) { const prevSel = selections[prevDay.id]?.date; if (prevSel) { expectedDependentDate = computeDependentDate(prevSel, day); const avail = availability[day.id] || []; hasSlotsForExpected = avail.some(a => expectedDependentDate && fns.isSameDay(a, expectedDependentDate)); } } return (
                    <div key={day.id} className={styles.bookingColumn}>
                        <BookingCard isLoadingServiceDetails={loadingDetails} title={details?.title || ''} content={details?.description || ''} duration={details?.duration || ''} />
                        <div className={styles.calendarWrapper}>
                            <div className={showCalendarSpinner ? styles.calendarDimmer : ''}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
                                    <DateCalendar
                                        value={sel ? sel.date : null}
                                        disablePast
                                        onChange={(date) => onSelectDate(date, day.id)}
                                        shouldDisableDate={(date) => {
                                            if (day.dependsOnPrev) {
                                                if (!expectedDependentDate) return true; // disabled until computed
                                                if (!fns.isSameDay(date, expectedDependentDate)) return true; // only expected day enabled
                                                return !hasSlotsForExpected; // keep disabled if no slots
                                            }
                                            return !(availability[day.id] || []).some(av => fns.isSameDay(av, date));
                                        }}
                                        onMonthChange={(d) => {
                                            if (!day.dependsOnPrev) {
                                                // Reset primary & dependent selections so new month behaves like initial load
                                                setSelections(prevSel => {
                                                    const next = { ...prevSel };
                                                    next[day.id] = { date: null, time: null };
                                                    const dependent = meetingConfig?.days[1];
                                                    if (dependent?.dependsOnPrev) next[dependent.id] = { date: null, time: null };
                                                    return next;
                                                });
                                                setErrors(prevErr => {
                                                    const next = { ...prevErr } as any;
                                                    delete next[day.id];
                                                    const dependent = meetingConfig?.days[1];
                                                    if (dependent?.dependsOnPrev) delete next[dependent.id];
                                                    return next;
                                                });
                                                fetchAvailabilityForDay(day.id, d);
                                            }
                                        }}
                                        onYearChange={(d) => {
                                            if (!day.dependsOnPrev) {
                                                setSelections(prevSel => {
                                                    const next = { ...prevSel };
                                                    next[day.id] = { date: null, time: null };
                                                    const dependent = meetingConfig?.days[1];
                                                    if (dependent?.dependsOnPrev) next[dependent.id] = { date: null, time: null };
                                                    return next;
                                                });
                                                setErrors(prevErr => {
                                                    const next = { ...prevErr } as any;
                                                    delete next[day.id];
                                                    const dependent = meetingConfig?.days[1];
                                                    if (dependent?.dependsOnPrev) delete next[dependent.id];
                                                    return next;
                                                });
                                                fetchAvailabilityForDay(day.id, d);
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                {dayErrors.date && <div className={styles.errorDataBlock}>{dayErrors.date}</div>}
                                <div className={styles.timeSelectorWrapper}><TimeSelector availableTime={timesForDay(day.id)} selectedTime={sel?.time || null} onSelectTime={(time) => onSelectTime(time, day.id)} /></div>
                                {dayErrors.time && <div className={styles.errorTimeBlock}>{dayErrors.time}</div>}
                            </div>
                            {showCalendarSpinner && <div className={styles.calendarLoader}><CircularProgress /></div>}
                        </div>
                    </div>
                ); })}
            </div>
            {showContact && (
                <>
                    <div className={styles.detailsRow}><span className={styles.addDetailsHeader}>Kontaktinformationer</span></div>
                    <div className={styles.bookingForm}>{(['email','emailRepeat','phone','phoneRepeat'] as ContactFieldKey[]).map(field => (
                        <div key={field} className={styles.inputLine}>
                            <span className={styles.inputLabel}>{field === 'emailRepeat' ? 'Gentag e-mail' : field === 'phoneRepeat' ? 'Gentag telefonnummer' : field === 'email' ? 'E-mail' : 'Telefonnummer'}</span>
                            <TextField variant='outlined' sx={!contact[field].error ? styles.inputNoBorder : {}} type={field.includes('email') ? 'email' : 'tel'} name={field} inputProps={{ style: styles.input }} placeholder={field === 'emailRepeat' ? 'Gentag e-mail' : field === 'phoneRepeat' ? 'Gentag telefonnummer' : field === 'email' ? 'E-mail' : 'Telefonnummer'} error={!!contact[field].error} helperText={contact[field].error} onChange={(e) => onContactChange(field, e.target.value)} value={contact[field].value} />
                        </div>))}
                    </div>
                </>
            )}
            <div className={styles.buttonRow}><Button variant='contained' className={styles.bookButton} disabled={submitting} onClick={onSubmit}>
                {submitting ? <CircularProgress size={32} sx={{ color: 'white' }} /> : 'Reservér'}
            </Button></div>
        </div>
    );
};

const styles = {
    content: css({
        marginTop: '4.5rem',
        width: '100%',
        paddingTop: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '.MuiFormHelperText-root.Mui-error': {
            position: 'absolute',
            top: '2rem',
            width: '100%',
            textAlign: 'right',
            marginLeft: 0,
        },
    }),
    bookingRow: css({
        display: 'flex',
        flexDirection: 'row',
        gap: '4.5rem',
        flexWrap: 'wrap',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: '2rem',
            alignItems: 'center',
        },
    }),
    bookingColumn: css({
        display: 'flex',
        flexDirection: 'column',
    }),
    detailsRow: css({
        display: 'flex',
        flexDirection: 'row',
        gap: '1rem',
    }),
    addDetailsHeader: css({
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '19px',
        textAlign: 'center',
        letterSpacing: '0.7px',
        textTransform: 'uppercase',
    }),
    bookingForm: css({
        display: 'flex',
        flexDirection: 'column',
        marginTop: '2.5rem',
    }),
    inputLine: css({
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '1rem',
        height: '4rem',
    }),
    inputNoBorder: {
        borderRadius: '4px 4px 2px 2px',
        border: 'none',
        '& fieldset': { border: 'none' },
        '& .MuiOutlinedInput-input': { backgroundColor: 'white !important' },
    },
    input: {
        width: '23rem',
        height: '1rem',
        padding: '7px',
        paddingTop: '5px',
        paddingBottom: '6px',
        background: '#F2F2F2',
        fontSize: '14px',
    },
    inputLabel: css({
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '19px',
        paddingBottom: '5px',
    }),
    bookButton: css({
        marginTop: '2rem !important',
    }),
    buttonRow: css({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        marginBottom: '2rem',
    }),
    calendarWrapper: css({
        position: 'relative',
    }),
    calendarDimmer: css({
        opacity: 0.65,
        pointerEvents: 'none',
    }),
    calendarLoader: css({
        position: 'absolute',
        top: '50%',
        right: '50%',
        transform: 'translate(50%, -50%)',
    }),
    timeSelectorWrapper: css({
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
    }),
    errorDataBlock: css({
        width: 320,
        textAlign: 'end',
        fontFamily: 'Segoe UI,regular',
        fontWeight: 400,
        fontSize: '0.75rem',
        lineHeight: '1.66',
        color: '#d32f2f',
        position: 'absolute',
        marginTop: '-1rem',
    }),
    errorTimeBlock: css({
        width: '100%',
        textAlign: 'center',
        fontFamily: 'Segoe UI,regular',
        fontWeight: 400,
        fontSize: '0.75rem',
        lineHeight: '1.66',
        color: '#d32f2f',
        position: 'static',
    }),
};

export default NewBookingPage;
